import React, { FunctionComponent, useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import { Button } from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import { useI18n } from "../../hooks/useI18n";

interface ConfirmDialogProps {
  visible?: boolean;
  titleText: string;
  bodyText?: string;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  showCancelButton?: boolean;
  onCancel: () => void;
  onConfirm: () => boolean | Promise<boolean>;
  onClose?: () => void;
  disableBackdropClick?: boolean;
}

export const ConfirmDialog: FunctionComponent<ConfirmDialogProps> = ({
  visible = true,
  titleText,
  bodyText = undefined,
  onClose,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  confirmDisabled = false,
  cancelDisabled = false,
  showCancelButton = true,
  disableBackdropClick = false,
  children
}) => {
  const { t } = useI18n();
  const [state, setState] = useState<boolean>(false);

  // Use translations for default button texts
  const defaultConfirmText = confirmText || t("common.actions.confirm");
  const defaultCancelText = cancelText || t("common.actions.cancel");

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      onCancel();
    }

    setState(false);
  };

  const handleCancel = () => {
    onCancel();
    setState(false);
  };

  const handleConfirm = async () => {
    const valid = await onConfirm();
    setState(!valid);
  };

  useEffect(() => {
    setState(visible);
  }, [visible]);

  let id = `confirm-dialog-${uuidv4()}`;

  return (
    <Dialog
      open={state}
      onClose={!cancelDisabled ? handleClose : () => {}}
      aria-labelledby={`${id}-title`}
      disableBackdropClick={disableBackdropClick}
      disableEscapeKeyDown={disableBackdropClick}
    >
      <DialogTitle id={`${id}-title`}>{titleText}</DialogTitle>
      <DialogContent>
        {bodyText ? <DialogContentText>{bodyText}</DialogContentText> : null}
        {children}
      </DialogContent>
      <DialogActions>
        {showCancelButton ? (
          <Button
            disabled={cancelDisabled}
            autoFocus
            onClick={handleCancel}
            color="primary"
          >
            {defaultCancelText}
          </Button>
        ) : null}
        <Button
          disabled={confirmDisabled}
          onClick={handleConfirm}
          color="primary"
          autoFocus
        >
          {defaultConfirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
